/**
 * ForensicToggle — Premium toggle for forensic access mode.
 *
 * Shows a lock/unlock button that authorized roles can use to activate
 * forensic mode (bypasses data masking for investigation).
 * ForensicBanner displays a red warning bar when active.
 *
 * @module components/common/ForensicToggle
 */

import React from 'react';
import { useForensicAccess } from '@/hooks/useForensicAccess';

interface ForensicToggleProps {
  context?: string;
  compact?: boolean;
  className?: string;
}

export const ForensicToggle: React.FC<ForensicToggleProps> = ({
  context = 'general',
  compact = false,
  className = '',
}) => {
  const { isForensicMode, canActivateForensicMode, activateForensicMode, deactivateForensicMode } = useForensicAccess();

  if (!canActivateForensicMode) return null;

  const handleToggle = () => {
    if (isForensicMode) {
      deactivateForensicMode();
    } else {
      activateForensicMode(context);
    }
  };

  return (
    <button
      onClick={handleToggle}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all border group ${
        isForensicMode
          ? 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100 shadow-sm shadow-red-200/50'
          : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-700'
      } ${className}`}
      title={isForensicMode ? 'Desactivar modo forense' : 'Activar modo forense (ver datos sin enmascarar)'}
    >
      {isForensicMode ? (
        <svg className="w-3.5 h-3.5 forensic-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )}
      {!compact && (
        <span>{isForensicMode ? 'Forense ON' : 'Forense'}</span>
      )}
    </button>
  );
};

/**
 * ForensicBanner — Top-of-page warning when forensic mode is active.
 */
export const ForensicBanner: React.FC = () => {
  const { isForensicMode, deactivateForensicMode, accessLog } = useForensicAccess();

  if (!isForensicMode) return null;

  return (
    <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 flex items-center justify-between text-xs shadow-sm">
      <div className="flex items-center gap-2.5">
        <div className="relative">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-white forensic-pulse" />
        </div>
        <span className="font-bold uppercase tracking-wider">Modo Forense Activo</span>
        <span className="opacity-75 hidden sm:inline">— Datos sin enmascarar visibles. Acceso registrado.</span>
        {accessLog && accessLog.accessedRecordIds.length > 0 && (
          <span className="opacity-60 font-mono bg-white/10 px-2 py-0.5 rounded">
            {accessLog.accessedRecordIds.length} registros
          </span>
        )}
      </div>
      <button
        onClick={deactivateForensicMode}
        className="px-3 py-1 bg-white/15 hover:bg-white/25 rounded-md font-bold transition-colors border border-white/20"
      >
        Desactivar
      </button>
    </div>
  );
};

export default ForensicToggle;
